// Cari Tediye Makbuzu — Tahsilat makbuzu ile birebir aynı tasarım, sadece ödeme yönü tersine.
// Yöntemler: nakit, banka havalesi, firma kredi kartı (expense_card), firma çeki (verilen),
// firma senedi (verilen), alışveriş çeki.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useCurrencies } from '../hooks/useCurrencies';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';
import CariCard from '../components/CariCard';
import QuickListManager from '../components/QuickListManager';
import { Button, Modal, FormField, ListPageHeader, ListPageStats } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';

const LOCAL_CURRENCY = 'TRY';

const PAYMENT_METHODS = [
  { value: 'cash',             label: 'Nakit',                  color: '#16a34a' },
  { value: 'bank_transfer',    label: 'Banka Havalesi',         color: '#3b82f6' },
  { value: 'expense_card',     label: 'Firma Kredi Kartı',      color: '#dc2626' },
  { value: 'check',            label: 'Firma Çeki (Verilen)',   color: '#7c3aed' },
  { value: 'note',             label: 'Firma Senedi (Verilen)', color: '#d97706' },
  { value: 'shopping_voucher', label: 'Alışveriş Çeki',         color: '#0284c7' },
];

const isCurrencyLocked = (method) => method === 'expense_card' || method === 'shopping_voucher';

const emptyLine = {
  payment_method: 'cash', amount: '', currency_code: 'TRY', exchange_rate: 1,
  cash_register_id: '', bank_account_id: '', expense_card_id: '',
  check_doc_number: '', check_serial_no: '', check_issuer_name: '',
  check_bank_name: '', check_branch_name: '', check_issue_date: '', check_due_date: '',
  shopping_voucher_id: '',
  notes: '',
};

function fmt(n) { return fmtAmount(n); }

function BalancePill({ account }) {
  const bal = parseFloat(account?.balance) || 0;
  const cur = account?.currency_code || 'TRY';
  const isDebit = bal > 0, isCredit = bal < 0;
  const color = isDebit ? '#dc2626' : isCredit ? '#16a34a' : '#64748b';
  const bg    = isDebit ? 'rgba(220,38,38,0.10)' : isCredit ? 'rgba(22,163,74,0.10)' : 'rgba(100,116,139,0.08)';
  const label = isDebit ? 'Cari bize borçlu' : isCredit ? 'Cari bizden alacaklı' : 'Bakiye sıfır';
  const dir   = isDebit ? '🔴 BORÇ' : isCredit ? '🟢 ALACAK' : '⚪ KAPALI';
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: bg,
                  border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
      <span style={{ fontWeight: 700, color, letterSpacing: '0.04em' }}>{dir}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 700, color, fontSize: 14 }}>
        {fmtAmount(Math.abs(bal))} {cur}
      </span>
    </div>
  );
}

function CariPaymentPage({ autoOpenId = null }) {
  const { currencies } = useCurrencies();
  const today = new Date().toISOString().slice(0, 10);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);

  // Lookups
  const [accounts, setAccounts] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [expenseCards, setExpenseCards] = useState([]);
  const [shoppingVouchers, setShoppingVouchers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [projects, setProjects] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [exchangeRates, setExchangeRates] = useState({});
  const [settings, setSettings] = useState({});

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'add' | 'edit'
  const [selected, setSelected] = useState(null);
  const [header, setHeader] = useState({
    account_id: '', date: today, description: '', notes: '',
    personnel_id: '', project_id: '', special_code1: '', special_code2: '',
  });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [showAccountList, setShowAccountList] = useState(false);
  const [accountListSearch, setAccountListSearch] = useState('');
  const [showQuickList, setShowQuickList] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/cari-payments', { params: { search } });
      if (r.data.success) setPayments(r.data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [search]);

  const fetchStats = useCallback(async () => {
    try { const r = await api.get('/cari-payments/stats'); if (r.data.success) setStats(r.data.data); } catch {}
  }, []);

  const refetchLookups = useCallback(async () => {
    try {
      const [a, c, b, ec, sv, p, pr, lk, st] = await Promise.all([
        api.get('/accounts', { params: { limit: 500 } }),
        api.get('/cash-registers'),
        api.get('/bank-accounts'),
        api.get('/expense-cards'),
        api.get('/shopping-vouchers', { params: { status: 'issued' } }).catch(() => ({ data: { success: false } })),
        api.get('/personnel', { params: { limit: 200 } }).catch(() => ({ data: { success: false } })),
        api.get('/invoices/projects').catch(() => ({ data: { success: false } })),
        api.get('/invoices/lookups/all').catch(() => ({ data: { success: false } })),
        api.get('/settings').catch(() => ({ data: { success: false } })),
      ]);
      if (a.data.success)  setAccounts((a.data.data.items || []).filter(x => x.is_active));
      if (c.data.success)  setCashRegisters(c.data.data.items || c.data.data || []);
      if (b.data.success)  setBankAccounts((b.data.data.items || []).filter(x => x.is_active));
      if (ec.data.success) setExpenseCards((ec.data.data.items || ec.data.data || []).filter(x => x.is_active));
      if (sv.data.success) setShoppingVouchers(sv.data.data.items || sv.data.data || []);
      if (p.data.success)  setPersonnel(p.data.data.items || p.data.data || []);
      if (pr.data.success) setProjects(pr.data.data.items || pr.data.data || []);
      if (lk.data.success) {
        const all = lk.data.data || {};
        setSpecialCodes1((all.special_codes || []).filter(s => s.code_type === 'special1'));
        setSpecialCodes2((all.special_codes || []).filter(s => s.code_type === 'special2'));
      }
      if (st.data.success) setSettings(st.data.data || {});
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { refetchLookups(); }, [refetchLookups]);

  // Döviz kurları — currency seçildikçe yenilenir
  useEffect(() => {
    const need = new Set(lines.map(l => l.currency_code).filter(c => c && c !== LOCAL_CURRENCY));
    need.forEach(async (code) => {
      if (exchangeRates[code]) return;
      try {
        const r = await api.get('/exchange/rates/lookup', { params: { date: header.date, currency_code: code } });
        if (r.data.success) setExchangeRates(prev => ({ ...prev, [code]: r.data.data }));
      } catch {}
    });
  }, [lines, header.date, exchangeRates]);

  const openNew = () => {
    setSelected(null);
    setHeader({ account_id: '', date: today, description: '', notes: '', personnel_id: '', project_id: '', special_code1: '', special_code2: '' });
    setLines([{ ...emptyLine, cash_register_id: cashRegisters[0]?.id || '' }]);
    setMode('add'); setMsg(null); setShowModal(true);
  };

  const openDetail = useCallback(async (rec) => {
    try {
      const r = await api.get('/cari-payments/' + rec.id);
      if (r.data.success) {
        const d = r.data.data;
        setSelected(d);
        setHeader({
          account_id: d.account_id, date: d.date?.slice(0, 10) || today,
          description: d.description || '', notes: d.notes || '',
          personnel_id: d.personnel_id || '', project_id: d.project_id || '',
          special_code1: d.special_code1 || '', special_code2: d.special_code2 || '',
        });
        setLines((d.lines || []).map(l => ({ ...l, amount: l.amount, currency_code: l.currency_code || 'TRY', exchange_rate: l.exchange_rate || 1 })));
        setMode('view'); setMsg(null); setShowModal(true);
      }
    } catch (e) { console.error(e); }
  }, [today]);

  useEffect(() => { if (autoOpenId) openDetail({ id: autoOpenId }); }, [autoOpenId, openDetail]);

  const closeModal = () => { setShowModal(false); setSelected(null); setMsg(null); };

  // Line helpers
  const addLine = () => setLines(p => [...p, { ...emptyLine, cash_register_id: cashRegisters[0]?.id || '' }]);
  const setLine = (i, k, v) => setLines(p => p.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const removeLine = (i) => setLines(p => p.filter((_, idx) => idx !== i));
  const setLinePaymentMethod = (i, method) => {
    setLines(p => p.map((l, idx) => {
      if (idx !== i) return l;
      const reset = { ...l, payment_method: method };
      // Yöntem değişince TRY-locked yöntemler için currency reset
      if (isCurrencyLocked(method)) { reset.currency_code = LOCAL_CURRENCY; reset.exchange_rate = 1; }
      return reset;
    }));
  };
  const setLineCashRegister = (i, id) => {
    const reg = cashRegisters.find(r => r.id === id);
    setLines(p => p.map((l, idx) => idx === i ? { ...l, cash_register_id: id, currency_code: reg?.currency_code || l.currency_code } : l));
  };
  const setLineBankAccount = (i, id) => {
    const ba = bankAccounts.find(b => b.id === id);
    setLines(p => p.map((l, idx) => idx === i ? { ...l, bank_account_id: id, currency_code: ba?.currency_code || l.currency_code } : l));
  };
  const setLineCurrency = (i, code) => {
    setLines(p => p.map((l, idx) => {
      if (idx !== i) return l;
      const r = exchangeRates[code];
      const rate = r?.selling_rate || r?.mid_rate || r?.buying_rate || (code === LOCAL_CURRENCY ? 1 : '');
      return { ...l, currency_code: code, exchange_rate: rate };
    }));
  };

  const lineLocal = (l) => (parseFloat(l.amount) || 0) * (parseFloat(l.exchange_rate) || 1);
  const total = lines.reduce((s, l) => s + lineLocal(l), 0);
  const hasFx = lines.some(l => l.currency_code && l.currency_code !== LOCAL_CURRENCY);

  const handleSave = async () => {
    const currentMode = mode;
    if (!header.account_id) { setMsg({ type: 'error', text: 'Cari seçin' }); return; }
    if (lines.length === 0) { setMsg({ type: 'error', text: 'En az bir ödeme satırı ekleyin' }); return; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i]; const no = i + 1;
      if (!l.payment_method) { setMsg({ type: 'error', text: `Satır ${no}: ödeme yöntemi seçin` }); return; }
      if (!l.amount || parseFloat(l.amount) <= 0) { setMsg({ type: 'error', text: `Satır ${no}: geçerli tutar girin` }); return; }
      if (l.payment_method === 'cash') {
        if (!l.cash_register_id) { setMsg({ type: 'error', text: `Satır ${no} (Nakit): kasa seçin` }); return; }
      } else if (l.payment_method === 'bank_transfer') {
        if (!l.bank_account_id) { setMsg({ type: 'error', text: `Satır ${no} (Banka): banka hesabı seçin` }); return; }
      } else if (l.payment_method === 'expense_card') {
        if (!l.expense_card_id) { setMsg({ type: 'error', text: `Satır ${no} (Kredi Kartı): kart seçin` }); return; }
      } else if (l.payment_method === 'check' || l.payment_method === 'note') {
        const tip = l.payment_method === 'check' ? 'Çek' : 'Senet';
        const eksik = [];
        if (!l.check_doc_number) eksik.push('Belge No');
        if (!l.check_due_date) eksik.push('Vade');
        if (!l.check_bank_name) eksik.push('Banka');
        if (eksik.length) { setMsg({ type: 'error', text: `Satır ${no} (${tip}): ${eksik.join(', ')} eksik` }); return; }
      } else if (l.payment_method === 'shopping_voucher') {
        // Tediye'de yeni çek üretiyoruz — sadece tutar yeterli
      }
    }
    setSaving(true); setMsg(null);
    try {
      const editing = currentMode === 'edit';
      const url = editing ? '/cari-payments/' + selected.id : '/cari-payments';
      const r = editing ? await api.put(url, { ...header, lines }) : await api.post(url, { ...header, lines });
      if (r.data.success) {
        fetchList(); fetchStats();
        if (editing) {
          await openDetail({ id: selected.id });
          setMsg({ type: 'success', text: 'Güncellendi' });
          setTimeout(() => setMsg(null), 2000);
        } else {
          closeModal();
        }
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Tediye makbuzu silinsin mi? Tüm bağlı hareketler geri alınacak.')) return;
    try {
      await api.delete('/cari-payments/' + selected.id);
      closeModal(); fetchList(); fetchStats();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  const isAdd  = mode === 'add';
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const isEditable = isAdd || isEdit;
  const sourceAccount = accounts.find(a => String(a.id) === String(header.account_id));
  const modalTitle = isAdd  ? 'Yeni Tediye Makbuzu'
                   : isEdit ? `Düzenle — ${selected?.payment_no || ''}`
                   :          (selected?.payment_no || 'Tediye Makbuzu');
  const enterEditMode = () => { setMode('edit'); setMsg(null); };

  return (
    <div className="stocks-page">
      <ListPageHeader title="Cari Tediye Makbuzları" count={payments.length}
        search={search} onSearch={setSearch} searchPlaceholder="Makbuz no, cari..."
        onRefresh={() => { fetchList(); fetchStats(); }} refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni Tediye</Button>} />

      <ListPageStats
        loading={stats == null}
        stats={[
          { label: 'Bu Ay', value: stats?.month_total || 0, sub: `${stats?.month_count || 0} adet`, color: 'primary' },
          { label: 'Bu Hafta', value: stats?.week_total || 0, sub: `${stats?.week_count || 0} adet`, color: 'neutral' },
          { label: 'Son 30g Adet', value: stats?.m30_count || 0, color: 'neutral' },
          { label: 'Toplam', value: stats?.all_total || 0, color: 'danger' },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable pageKey="cari-payments-list" tableClassName="stocks-table" rowClassName="stocks-table-row"
          loading={loading} emptyText="Tediye yok" data={payments} rowKey="id"
          onRowClick={r => openDetail(r)}
          columns={[
            { id: 'no', header: 'Belge No', accessor: 'payment_no', sortable: true, width: 170,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)' }}>{r.payment_no}</span> },
            { id: 'date', header: 'Tarih', accessor: 'date', sortable: true, width: 110,
              cell: r => <span>{r.date ? new Date(r.date).toLocaleDateString('tr-TR') : '—'}</span> },
            { id: 'account', header: 'Cari', accessor: 'account_title', sortable: true,
              cell: r => <><strong>{r.account_title}</strong>{r.account_code && <div style={{ fontSize:'0.74rem', color:'var(--text-tertiary)' }}>{r.account_code}</div>}</> },
            { id: 'desc', header: 'Açıklama', accessor: 'description', cell: r => r.description || '—' },
            { id: 'total', header: 'Tutar', accessor: r => parseFloat(r.total_amount) || 0, sortable: true, width: 160,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#dc2626' }}>{fmt(r.total_amount)} TRY</span> },
          ]} />
      </div>

      {showModal && (
        <Modal icon={null} title={modalTitle} size="xl" onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {isView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isEditable && <Button variant="ghost" onClick={isEdit ? () => openDetail({ id: selected.id }) : closeModal}>
                  {isEdit ? 'İptal' : 'Vazgeç'}
                </Button>}
                {isEditable && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={handleSave}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Kapat', icon: '✓', onClick: handleSave },
                    ]}
                  />
                )}
                {isView && <Button onClick={enterEditMode}>✎ Düzenle</Button>}
              </div>
            </>
          }>

          {/* Hero strip — view modunda */}
          {isView && selected && (
            <CardHero
              icon="💸"
              code={selected.payment_no}
              title={selected.account_title}
              subtitle={selected.date ? new Date(selected.date).toLocaleDateString('tr-TR') : null}
              statusBadge={<CardStatusBadge preset="approved" label="Onaylı" />}
              kpis={[
                { label: 'Toplam Tutar', value: fmt(selected.total_amount), color: 'danger', sublabel: 'TRY' },
                selected.personnel_name && { label: 'Personel', value: selected.personnel_name.substring(0, 20), color: 'neutral' },
              ].filter(Boolean)}
            />
          )}

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          {/* Header alanları */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <FormField label="Belge No">
              <input disabled value={isAdd ? 'Otomatik' : (selected?.payment_no || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }} />
            </FormField>
            <FormField label="Tarih" required>
              <input type="date" value={header.date} onChange={e => setHeader({ ...header, date: e.target.value })} disabled={isView} />
            </FormField>
            {/* CARI SEÇİMİ — tam genişlik */}
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Cari" required>
                <CariCard cari={sourceAccount} disabled={isView} label="Cari" required
                  onPick={() => { setAccountListSearch(''); setShowAccountList(true); }} />
                {sourceAccount && <BalancePill account={sourceAccount} />}
              </FormField>
            </div>
            <FormField label="Açıklama" fullWidth>
              <input value={header.description} onChange={e => setHeader({ ...header, description: e.target.value })} disabled={isView} placeholder="Örn: Tedarikçi ödemesi" />
            </FormField>
            <FormField label="Personel">
              <select value={header.personnel_id} onChange={e => setHeader({ ...header, personnel_id: e.target.value })} disabled={isView}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <FormField label={<>Proje {isEditable && <button className="quick-add-btn" onClick={() => setShowQuickList('project')} type="button">+</button>}</>}>
              <select value={header.project_id} onChange={e => setHeader({ ...header, project_id: e.target.value })} disabled={isView}>
                <option value="">Seçiniz</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.code ? p.code + ' - ' : ''}{p.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 1 {isEditable && <button className="quick-add-btn" onClick={() => setShowQuickList('special1')} type="button">+</button>}</>}>
              <select value={header.special_code1} onChange={e => setHeader({ ...header, special_code1: e.target.value })} disabled={isView}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {isEditable && <button className="quick-add-btn" onClick={() => setShowQuickList('special2')} type="button">+</button>}</>}>
              <select value={header.special_code2} onChange={e => setHeader({ ...header, special_code2: e.target.value })} disabled={isView}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>

          {/* Ödeme Yöntemleri başlığı */}
          <div style={{ marginBottom: 12, fontSize: '0.95rem', fontWeight: 700 }}>Ödeme Yöntemleri</div>
          <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, maxHeight: 380, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 1280 }}>
              <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', width: 30 }}>#</th>
                  <th style={{ padding: '8px', textAlign: 'left', width: 180 }}>Yöntem</th>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: 280 }}>Detay</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 120 }}>Tutar</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: 80 }}>Döviz</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 90 }}>Kur</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 130 }}>YPB (TRY)</th>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: 180 }}>Açıklama</th>
                  {isEditable && <th style={{ width: 40 }}></th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const method = PAYMENT_METHODS.find(p => p.value === l.payment_method);
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {isEditable ? (
                          <select value={l.payment_method} onChange={e => setLinePaymentMethod(i, e.target.value)}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem' }}>
                            {PAYMENT_METHODS
                              .filter(p => p.value !== 'shopping_voucher' || String(settings.shopping_voucher_enabled || 'false').toLowerCase() === 'true')
                              .map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        ) : (
                          <span style={{ background: (method?.color || '#64748b') + '22', color: method?.color, padding: '3px 9px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
                            {method?.label || l.payment_method}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {l.payment_method === 'cash' && (isEditable ? (
                          <>
                            <select value={l.cash_register_id || ''} onChange={e => setLineCashRegister(i, e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem', borderColor: !l.cash_register_id ? 'var(--danger)' : undefined }}>
                              <option value="">Kasa seç *</option>
                              {cashRegisters.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
                            </select>
                            {(() => {
                              const reg = cashRegisters.find(r => r.id === l.cash_register_id);
                              if (reg && l.currency_code && reg.currency_code !== l.currency_code) {
                                return <div style={{ fontSize:'0.7rem', color:'var(--danger)', marginTop: 2 }}>
                                  ⚠ Kasa {reg.currency_code} — {l.currency_code} kabul etmez.
                                </div>;
                              }
                              return null;
                            })()}
                          </>
                        ) : <span>{l.cash_register_name || '—'}</span>)}

                        {l.payment_method === 'bank_transfer' && (isEditable ? (
                          <>
                            <select value={l.bank_account_id || ''} onChange={e => setLineBankAccount(i, e.target.value)}
                              style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem', borderColor: !l.bank_account_id ? 'var(--danger)' : undefined }}>
                              <option value="">Banka hesabı seç *</option>
                              {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name} ({b.currency_code})</option>)}
                            </select>
                          </>
                        ) : <span>{l.bank_account_name || '—'}</span>)}

                        {l.payment_method === 'expense_card' && (isEditable ? (
                          <select value={l.expense_card_id || ''} onChange={e => setLine(i, 'expense_card_id', e.target.value)}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem', borderColor: !l.expense_card_id ? 'var(--danger)' : undefined }}>
                            <option value="">Kredi kartı seç *</option>
                            {expenseCards.map(c => <option key={c.id} value={c.id}>{c.card_name}{c.last_four ? ' ****' + c.last_four : ''}{c.bank_name ? ' — ' + c.bank_name : ''}</option>)}
                          </select>
                        ) : <span>{l.expense_card_name || '—'}{l.expense_card_last_four ? ' ****' + l.expense_card_last_four : ''}</span>)}

                        {(l.payment_method === 'check' || l.payment_method === 'note') && (isEditable ? (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                            <input placeholder="Belge No *" required value={l.check_doc_number || ''} onChange={e => setLine(i, 'check_doc_number', e.target.value)}
                              style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_doc_number ? 'var(--danger)' : undefined }} />
                            <input type="date" required value={l.check_due_date || ''} onChange={e => setLine(i, 'check_due_date', e.target.value)}
                              style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_due_date ? 'var(--danger)' : undefined }} title="Vade *" />
                            <input placeholder="Banka *" required value={l.check_bank_name || ''} onChange={e => setLine(i, 'check_bank_name', e.target.value)}
                              style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_bank_name ? 'var(--danger)' : undefined }} />
                          </div>
                        ) : (
                          <span>
                            <strong>{l.check_doc_number || '—'}</strong>
                            {l.check_bank_name && <span style={{ color:'var(--text-secondary)' }}> · {l.check_bank_name}</span>}
                            {l.check_due_date && <span style={{ color:'var(--text-tertiary)' }}> · Vade: {new Date(l.check_due_date).toLocaleDateString('tr-TR')}</span>}
                          </span>
                        ))}

                        {l.payment_method === 'shopping_voucher' && (isEditable ? (
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap: 6 }}>
                            <input type="text" disabled value="Yeni çek üretilecek" title="Belge no otomatik"
                              style={{ padding:'4px 8px', fontSize:'0.78rem', fontStyle:'italic', color:'var(--text-tertiary)' }} />
                            <input type="date" value={l.shopping_voucher_expiry_date || ''}
                              onChange={e => setLine(i, 'shopping_voucher_expiry_date', e.target.value)}
                              title="Son Kullanma Tarihi (opsiyonel)"
                              style={{ padding:'4px 8px', fontSize:'0.78rem' }} />
                          </div>
                        ) : (
                          <span>
                            <strong>{l.shopping_voucher_no || '—'}</strong>
                            {l.shopping_voucher_expiry_date && <span style={{ color:'var(--text-tertiary)' }}> · son: {new Date(l.shopping_voucher_expiry_date).toLocaleDateString('tr-TR')}</span>}
                          </span>
                        ))}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" step="0.01" min="0" value={l.amount || ''} onChange={e => setLine(i, 'amount', e.target.value)} disabled={isView}
                          style={{ width: '100%', padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }} />
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <select value={l.currency_code || LOCAL_CURRENCY}
                          onChange={e => setLineCurrency(i, e.target.value)}
                          disabled={isView || isCurrencyLocked(l.payment_method)}
                          title={isCurrencyLocked(l.payment_method) ? 'Bu yöntem sadece TRY' : ''}
                          style={{ width: '100%', padding: '4px 6px', fontSize: '0.78rem' }}>
                          {currencies.length === 0 && <option value="TRY">TRY</option>}
                          {currencies.map(c => {
                            if (c.code === LOCAL_CURRENCY) return <option key={c.code} value={c.code}>{c.code}</option>;
                            const r = exchangeRates[c.code];
                            const rate = r?.selling_rate || r?.mid_rate || r?.buying_rate;
                            return <option key={c.code} value={c.code}>
                              {c.code}{rate ? ` (${Number(rate).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })})` : ''}
                            </option>;
                          })}
                        </select>
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <input type="number" step="0.000001" min="0"
                          value={l.exchange_rate || (l.currency_code === LOCAL_CURRENCY ? 1 : '')}
                          onChange={e => setLine(i, 'exchange_rate', e.target.value)}
                          disabled={isView || l.currency_code === LOCAL_CURRENCY || isCurrencyLocked(l.payment_method)}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.82rem',
                                   color: l.currency_code !== LOCAL_CURRENCY ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
                        {fmt(lineLocal(l))}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="text" value={l.notes || ''} onChange={e => setLine(i, 'notes', e.target.value)} disabled={isView}
                          placeholder={isEditable ? 'Satır açıklaması...' : ''}
                          style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem' }} />
                      </td>
                      {isEditable && (
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ALT: Sol = Satır Ekle + Notlar; Sağ = Toplamlar */}
          <div className="receipt-bottom-row" style={{ display: 'flex', gap: 16, marginTop: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              {isEditable && (
                <Button variant="ghost" size="sm" onClick={addLine} className="receipt-add-line">+ Satır Ekle</Button>
              )}
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-tertiary)', margin: '10px 0 4px' }}>Notlar</label>
              <textarea rows="5" value={header.notes} onChange={e => setHeader({ ...header, notes: e.target.value })} disabled={!isEditable}
                className="ui-input"
                style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div className="receipt-totals-panel" style={{ width: 380, flexShrink: 0 }}>
              {(() => {
                const groups = {};
                lines.forEach(l => {
                  const m = l.payment_method;
                  if (!groups[m]) groups[m] = { byCcy: {}, local: 0 };
                  const ccy = l.currency_code || LOCAL_CURRENCY;
                  groups[m].byCcy[ccy] = (groups[m].byCcy[ccy] || 0) + (parseFloat(l.amount) || 0);
                  groups[m].local += lineLocal(l);
                });
                const groupRows = Object.entries(groups).filter(([, g]) => g.local > 0);
                return (
                  <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <tbody>
                        {groupRows.map(([m, g]) => {
                          const method = PAYMENT_METHODS.find(p => p.value === m);
                          const ccyEntries = Object.entries(g.byCcy).filter(([, v]) => v > 0);
                          return (
                            <tr key={m} style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                              <td style={{ padding: '8px 14px', fontSize: '0.85rem', color: method?.color, fontWeight: 500 }}>
                                {method?.label || m}:
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                                  {ccyEntries.map(([c, v]) => `${fmt(v)} ${c}`).join(' + ')}
                                </div>
                              </td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.95rem', color: method?.color, fontWeight: 700 }}>
                                {fmt(g.local)} <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>TRY</span>
                              </td>
                            </tr>
                          );
                        })}
                        <tr style={{ background: 'rgba(220,38,38,0.10)', fontWeight: 700 }}>
                          <td style={{ padding: '12px 14px', fontSize: '0.92rem', color: '#dc2626' }}>GENEL TOPLAM (YPB):</td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontFamily: 'monospace', fontSize: '1.05rem', color: '#dc2626' }}>
                            {fmt(total)} TRY
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>

          {hasFx && (
            <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', marginTop: 6, textAlign: 'right' }}>
              ℹ YPB (Yerli Para Birimi) tutarlar TRY karşılığıdır
            </div>
          )}
        </Modal>
      )}

      {/* QuickListManager — Proje/Özel kodlar */}
      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'project' ? 'Proje' : showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'project' ? projects : showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase={showQuickList === 'project' ? '/invoices/projects' : '/invoices/special-codes'}
          extraBody={showQuickList === 'project' ? {} : { code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={refetchLookups}
        />
      )}

      {/* Cari Listesi modal */}
      {showAccountList && (() => {
        const q = (accountListSearch || '').toLowerCase().trim();
        const filtered = (accounts || []).filter(a => !q ||
          (a.code || '').toLowerCase().includes(q) ||
          (a.title || '').toLowerCase().includes(q) ||
          (a.tax_number || '').includes(q) ||
          (a.phone || '').includes(q));
        const cariCols = [
          { id: 'code',  header: 'Kod',   accessor: 'code',  sortable: true, width: 110,
            cell: a => <span className="stock-code">{a.code}</span> },
          { id: 'title', header: 'Ünvan', accessor: 'title', sortable: true,
            cell: a => <span className="stock-name-cell">{a.title}</span> },
          { id: 'phone', header: 'Telefon', sortable: true, width: 130,
            accessor: a => a.phone || a.mobile || '',
            cell: a => <span className="stock-secondary">{a.phone || a.mobile || '—'}</span> },
          { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.balance) || 0, sortable: true, width: 170,
            headerStyle: { textAlign:'right' }, cellStyle: { textAlign:'right' },
            cell: a => {
              const b = parseFloat(a.balance) || 0;
              const color = b > 0 ? 'var(--danger)' : b < 0 ? 'var(--success)' : 'var(--text-tertiary)';
              const sfx = b > 0 ? ' B' : b < 0 ? ' A' : '';
              return <span style={{ fontFamily:'monospace', color, fontWeight: 600 }}>{fmt(Math.abs(b))} {a.currency_code || 'TRY'}{sfx}</span>;
            } },
        ];
        return (
          <div className="modal-overlay" onClick={() => setShowAccountList(false)}>
            <div className="modal-box" style={{ maxWidth: 980, width:'100%', maxHeight:'85vh', display:'flex', flexDirection:'column', padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border-secondary)', display:'flex', alignItems:'center', gap: 10, flexWrap:'wrap' }}>
                <h3 style={{ margin: 0, fontSize:'1rem', fontWeight: 700 }}>Cari Listesi</h3>
                <span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius: 12, background:'var(--accent-glow)', color:'var(--accent-primary)', fontWeight: 600 }}>{filtered.length} kayıt</span>
                <input autoFocus placeholder="Kod, ünvan, telefon, VKN..." value={accountListSearch} onChange={e => setAccountListSearch(e.target.value)}
                  style={{ flex: 1, padding:'7px 12px', borderRadius: 8, border:'1px solid var(--border-secondary)', background:'var(--bg-secondary)', minWidth: 200 }} />
                <button onClick={() => setShowAccountList(false)} title="Kapat"
                  style={{ width: 32, height: 32, padding: 0, border:'1px solid var(--border-secondary)', borderRadius: 8, background:'var(--bg-card)', cursor:'pointer' }}>✕</button>
              </div>
              <div style={{ flex: 1, overflow:'auto' }} className="stocks-table-wrap">
                <DataTable pageKey="cari-payment-picker" tableClassName="stocks-table" rowClassName="stocks-table-row"
                  data={filtered} rowKey="id"
                  onRowClick={a => { setHeader(prev => ({ ...prev, account_id: a.id })); setShowAccountList(false); }}
                  columns={cariCols} />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default CariPaymentPage;
